import { defineComponent } from '@nuxtjs/composition-api';
import type { PropType } from 'vue';

import { keys } from './accessibility';
import type { BravadoSelectDropdownOption } from './types';
import JobsCandidateMutateMixin from '@/components/partials/jobs/jobsCandidateMutateMixin';

interface ComponentData {
  showOptions: boolean;
}

export default defineComponent({
  name: 'BravadoSelectDropdownHandlerMixin',
  mixins: [JobsCandidateMutateMixin],
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Array as PropType<BravadoSelectDropdownOption[]>,
      default: () => [] as BravadoSelectDropdownOption[],
    },
    id: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
    hint: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    options: {
      type: Array as PropType<BravadoSelectDropdownOption[]>,
      default: () => [] as BravadoSelectDropdownOption[],
    },
    multi: {
      type: Boolean,
      default: false,
    },
    withCounter: {
      type: Boolean,
      default: false,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    isInvalid: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    getIsRemovable: {
      type: Function as PropType<(option: BravadoSelectDropdownOption) => boolean>,
      default: () => true,
    },
  },
  data(): ComponentData {
    return {
      showOptions: false,
    };
  },
  computed: {
    sortedOptions(): BravadoSelectDropdownOption[] {
      return [...this.options].sort((a, b) =>
        a.selectDropdownTitle.toLowerCase() < b.selectDropdownTitle.toLowerCase() ? -1 : 1,
      );
    },
  },
  methods: {
    handleCloseOptions() {
      this.showOptions = false;
    },

    handleOpenOptions() {
      this.showOptions = true;
    },

    handleToggleOption(option: BravadoSelectDropdownOption) {
      const optionIsSelected = this.value.find(
        selectedOption => selectedOption.selectDropdownValue === option.selectDropdownValue,
      );
      const methodToUse = this.multi ? 'updateMultiModel' : 'updateSingleModel';
      const newValue = this[methodToUse](option, !!optionIsSelected);
      this.$emit('input', newValue);
    },

    handleKeyDown(event: KeyboardEvent) {
      const callbacks = {
        [keys.ARROW_UP.keyName]: () => this.handleOpenOptions(),
        [keys.ARROW_DOWN.keyName]: () => this.handleOpenOptions(),
        [keys.ESCAPE.keyName]: () => this.handleCloseOptions(),
      };

      callbacks[event.key]?.();
    },

    updateMultiModel(option: BravadoSelectDropdownOption, optionIsSelected: boolean) {
      return optionIsSelected
        ? this.value.filter(selectedOption => selectedOption.selectDropdownValue !== option.selectDropdownValue)
        : [...this.value, option];
    },

    updateSingleModel(option: BravadoSelectDropdownOption, optionIsSelected: boolean) {
      return optionIsSelected ? [] : [option];
    },
  },
});
